// packages/report-components/src/ReportNarrative.tsx
//
// THE ANALYST'S WORDS, DRAWN FROM THE STORE.
//
// Phase 2 of ADR-0257 (ADR-0305) took the text of a composed report out of its
// compiled program and put it on the section, keyed by block:
//
//   { "<block_key>": [ { component, occurrence, fields }, … ] }
//
// Nothing read it. This is the reader. It walks the groups in order — which is
// the EMIT order of the components in that block, not a sort (`narrative_data.
// document_from_slots` says why: sorting a real deep dive puts the advice above
// the prose that motivates it) — and draws each one with the component the
// group names.
//
// ── THREE RULES, AND EACH ONE HAS A FAILURE BEHIND IT ────────────────────
//
// 1. NAMED PROPS, NEVER A SPREAD. `{...fields}` would turn the render contract
//    into a data decision: a stored key `dangerouslySetInnerHTML` would become
//    raw HTML, and a stored `onClick` would become a handler. The store is
//    written by a server that validates, but the whole point of phase 3 is that
//    these values now render on the APP's origin, where the session lives,
//    instead of in a null-origin iframe. The blast radius grew, so the reading
//    stays explicit: every component below names the props it reads, and a key
//    that is not in that list is dropped without a word to the DOM.
//
// 2. TEXT IS A TEXT NODE. No `dangerouslySetInnerHTML`, no markdown renderer,
//    ever. The values are written by a model and influenced by a tenant. The
//    components below take strings and React makes them text nodes; that is the
//    entire mechanism, and `narrative_contract.test.tsx` pins it with a
//    positive control (a fixture whose value is `<img onerror=…>` must appear
//    on screen as those characters).
//
// 3. AN EMPTY FIELD DRAWS NOTHING — never the word "undefined", never an empty
//    bullet. `document_from_extracted_slots` already drops blanks on the way in,
//    so a missing field means "this slot was never filled", and a component that
//    rendered `String(undefined)` would put that in a customer's report.
//
// ── WHAT IS TEXT AND WHAT IS SHAPE ──────────────────────────────────────
// `fields` carries the WORDS. The line budgets, the font sizes, the eyebrow and
// the tinted band are SHAPE: the server computed them with `analysis_allocation`
// when it emitted the block, and re-deriving them here would be a second source
// of truth for the one number that decides whether a cell overflows. They
// therefore travel beside the words as `props`, computed by the same function,
// and this module reads them through the same allowlist discipline.

import * as React from "react";
import { Callout } from "./Callout";
import { ExecSummary } from "./ExecSummary";
import { KeyTakeaways, type KeyTakeaway, type TakeawaySentiment } from "./KeyTakeaways";
import { RecommendationList, type Recommendation } from "./RecommendationList";
import { SectionHeader } from "./SectionHeader";
import { SectionIntro } from "./SectionIntro";

/** One component group of a stored narrative document.
 *
 *  `occurrence` stays explicit rather than being the position in the list: an
 *  agent-deep-dive block emits one group per finding, so a block genuinely
 *  holds several `KeyTakeaways`, and encoding that as an index would re-create
 *  the structural addressing phase 2 was built to remove. */
export type NarrativeGroup = {
  component: string;
  occurrence?: number;
  /** The analyst's words. Only the keys the component below names are read. */
  fields?: Record<string, unknown> | null;
  /** The shape the SERVER budgeted for those words — line counts, font sizes,
   *  the eyebrow, the tinted band. Never re-derived here. */
  props?: Record<string, unknown> | null;
};

export type ReportNarrativeProps = {
  groups: readonly NarrativeGroup[] | null | undefined;
  /** Told when a group names a component this build cannot draw — an older
   *  frontend against a newer backend. A COMPONENT NAME is an identifier from
   *  the server's own whitelist, never author text, so naming it in a log
   *  quotes nobody. */
  onUnknownComponent?: (component: string) => void;
};

/** The words of one block, in emit order. */
export function ReportNarrative({
  groups,
  onUnknownComponent,
}: ReportNarrativeProps): React.ReactElement | null {
  if (!groups || groups.length === 0) return null;
  const drawn: React.ReactNode[] = [];
  groups.forEach((group, i) => {
    const draw = NARRATIVE_COMPONENTS[group.component];
    if (!draw) {
      if (onUnknownComponent) onUnknownComponent(String(group.component));
      return;
    }
    const node = draw(group.fields ?? {}, group.props ?? {});
    if (node !== null) {
      drawn.push(
        <React.Fragment key={`${group.component}-${group.occurrence ?? 0}-${i}`}>{node}</React.Fragment>,
      );
    }
  });
  if (drawn.length === 0) return null;
  return <>{drawn}</>;
}

// ---------------------------------------------------------------------------
// The readers
// ---------------------------------------------------------------------------

type Draw = (
  fields: Record<string, unknown>,
  props: Record<string, unknown>,
) => React.ReactNode;

/** A stored value that is a non-blank string, or undefined.
 *
 *  Blank is treated as absent on purpose: the adapter emits every narrative
 *  slot as an empty literal and the pass fills it, so `""` in a document is a
 *  slot that was never written. Drawing it would give a component an empty
 *  heading where it expects a sentence. */
function str(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

/** A stored value that is a finite number, or undefined. The line budgets are
 *  integers from the server; anything else is dropped rather than coerced — a
 *  `NaN` reaching a clamp turns a cell's height into nothing. */
function num(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function strings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  // A GAP in a string list is stored as `null` (`document_from_slots` is
  // explicit about it: a gap is not empty text), so the filter is what keeps a
  // hole from becoming a bullet reading "null".
  return value.filter((v): v is string => typeof v === "string" && v.trim() !== "");
}

const SENTIMENTS = new Set(["positive", "negative", "neutral"]);

/** `KeyTakeaways.items` — the LLM's title and body beside the deterministic
 *  chip the adapter baked. An item with no title is dropped: the component's
 *  `title` is required, and a takeaway with a body and no title draws a
 *  headless row. */
function takeaways(value: unknown): KeyTakeaway[] {
  if (!Array.isArray(value)) return [];
  const out: KeyTakeaway[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object") continue;
    const item = raw as Record<string, unknown>;
    const title = str(item.title);
    if (!title) continue;
    const sentiment = str(item.sentiment);
    out.push({
      title,
      body: str(item.body),
      value: str(item.value),
      sentiment: sentiment && SENTIMENTS.has(sentiment) ? (sentiment as TakeawaySentiment) : undefined,
    });
  }
  return out;
}

/** `RecommendationList.items` — title and body only. The component also knows
 *  `priority`, `owner`, `dueBy`, `expectedImpact` and `evidenceSectionId`, and
 *  NONE of them is a narrative field: nothing produces them on the composed
 *  route, so reading them here would be a door held open for a value the
 *  server never validates. */
function recommendations(value: unknown): Recommendation[] {
  if (!Array.isArray(value)) return [];
  const out: Recommendation[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object") continue;
    const item = raw as Record<string, unknown>;
    const title = str(item.title);
    const body = str(item.body);
    if (!title && !body) continue;
    out.push({ title: title ?? "", body: body ?? "" });
  }
  return out;
}

/** component name → how it is drawn. The mirror of `NARRATIVE_FIELDS` in
 *  `services/reports/narrative_data.py`; a component missing here draws
 *  nothing and says so through `onUnknownComponent`, rather than throwing in a
 *  reader's face. */
const NARRATIVE_COMPONENTS: Record<string, Draw> = {
  ExecSummary: (fields, props) => {
    const thesis = str(fields.thesis);
    const summary = str(fields.summary);
    const bullets = strings(fields.bullets);
    if (!thesis && !summary && bullets.length === 0) return null;
    return (
      <ExecSummary
        thesis={thesis}
        summary={summary}
        bullets={bullets.length > 0 ? bullets : undefined}
        heading={str(props.heading)}
        thesisLines={num(props.thesisLines)}
        summaryLines={num(props.summaryLines)}
        thesisFontPx={num(props.thesisFontPx)}
        thesisLinePx={num(props.thesisLinePx)}
        leadInPx={num(props.leadInPx)}
      />
    );
  },

  SectionIntro: (fields, props) => {
    const text = str(fields.text);
    if (!text) return null;
    // `fontPx` / `linePx` travel too, or the `standfirst` kind reads as body
    // prose here and as a standfirst in the PDF. They were missed when the
    // kind shipped on 2026-09-29 and the two surfaces drew different pages
    // for a day, with every test green: a prop the server emits and this
    // mirror does not read is invisible from both sides.
    const intro = (
      <SectionIntro
        text={text}
        lines={num(props.lines)}
        fontPx={num(props.fontPx)}
        linePx={num(props.linePx)}
      />
    );
    // The `focus` kind: a tinted band with the block's label on it, holding one
    // sentence. The band is SHAPE — the adapter wraps the same SectionIntro in
    // `<Callout variant="band" title=…>` — so it arrives as a prop rather than
    // as a second component in the document, which is also why `Callout` is not
    // in the server's narrative whitelist.
    const band = props.band as { title?: unknown } | null | undefined;
    if (!band || typeof band !== "object") return intro;
    return (
      <Callout variant="band" title={str(band.title)}>
        {intro}
      </Callout>
    );
  },

  KeyTakeaways: (fields, props) => {
    const items = takeaways(fields.items);
    if (items.length === 0) return null;
    return <KeyTakeaways items={items} title={str(props.title)} bodyLines={num(props.bodyLines)} />;
  },

  RecommendationList: (fields, props) => {
    const items = recommendations(fields.items);
    if (items.length === 0) return null;
    return (
      <RecommendationList
        items={items}
        heading={str(props.heading) ?? "Recommendations"}
        bodyLines={num(props.bodyLines)}
      />
    );
  },

  // The two below belong to the TEMPLATE route, not the composed one: measured
  // 2026-09-19, the adapter emits `<SectionHeader title …/>` and `<DataPoint
  // label metric/>` with neither slot. They are in the server's field map so
  // that its drift test asserts the whole whitelist rather than a subset, and
  // they are here for the same reason — dropping them would make this map a
  // subset of that one, which is exactly the drift both are guarding.
  SectionHeader: (fields, props) => {
    const conclusion = str(fields.conclusion);
    if (!conclusion) return null;
    return <SectionHeader title={str(props.title) ?? ""} conclusion={conclusion} />;
  },

  // `DataPoint.contextNote` has no standalone drawing: a context note without
  // its metric is not a thing on a page. The reading room draws a scorecard
  // from its hook answer, with the note passed there. Declared with a null
  // body so an unknown-component log line stays honest about what is REALLY
  // unknown.
  DataPoint: () => null,
};

/** The component names this layer can draw — exported so a test can compare it
 *  with the server's `NARRATIVE_FIELDS` instead of two lists drifting quietly. */
export const NARRATIVE_COMPONENT_NAMES = Object.keys(NARRATIVE_COMPONENTS).sort();
